/**
 * Hero Animations Component
 * Handles scroll-based blur effects for hero backgrounds
 * Adapted from dawn parallax-images.js
 */

if (!customElements.get('hero-animations')) {
  class HeroAnimations extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLElement | null} */
      this.blurElement = null;
      this.isVisible = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.enableBlur = this.dataset.enableBlur === 'true';
      this.enableParallax = this.dataset.enableParallax === 'true';
      /** @type {EventListener | null} */
      this.scrollHandler = null;
      /** @type {EventListener | null} */
      this.resizeHandler = null;
    }

    connectedCallback() {
      this.blurElement = /** @type {HTMLElement | null} */ (this.querySelector('[data-blur-target]'));
      
      if (!this.blurElement) return;

      // Only set up if at least one effect is enabled
      const hasEffects = this.enableBlur || this.enableParallax;
      if (!hasEffects) return;

      this.setupIntersectionObserver();

      if (!this.reduceMotion) {
        this.setupScrollListener();
        this.setupResizeListener();
      }
    }

    disconnectedCallback() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
    }

    /**
     * Sets up IntersectionObserver to detect when component enters viewport
     */
    setupIntersectionObserver() {
      const observerOptions = {
        root: null,
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.1,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            this.classList.add('is-visible');

            // Initial effects calculation
            if (!this.reduceMotion && (this.enableBlur || this.enableParallax)) {
              requestAnimationFrame(() => this.updateEffects());
            }
          }
        });
      }, observerOptions);

      observer.observe(this);
    }

    /**
     * Sets up throttled scroll listener for scroll effects
     */
    setupScrollListener() {
      this.scrollHandler = this.throttle(() => {
        if (!this.isVisible) return;
        requestAnimationFrame(() => this.updateEffects());
      }, 16); // ~60fps

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    setupResizeListener() {
      this.resizeHandler = this.throttle(() => {
        if (!this.isVisible) return;
        requestAnimationFrame(() => this.updateEffects());
      }, 100);

      window.addEventListener('resize', this.resizeHandler, { passive: true });
    }

    /**
     * Calculates how much of the element has been scrolled through
     * @returns {number} Progress from 0 to 1
     */
    calculateScrollProgress() {
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const elementHeight = this.offsetHeight;

      // Element is below viewport
      if (rect.top > viewportHeight) return 0;

      // Element is above viewport
      if (rect.bottom < 0) return 1;

      // Calculate progress through the element
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const totalTravelDistance = viewportHeight + elementHeight;
      const distanceTraveled = viewportHeight - rect.top;

      const progress = distanceTraveled / totalTravelDistance;
      return Math.max(0, Math.min(1, progress));
    }

    /**
     * Updates blur and/or parallax effects based on scroll position
     */
    updateEffects() {
      const scrollProgress = this.calculateScrollProgress();
      
      // Always set scroll progress for potential use
      this.style.setProperty('--scroll-progress', scrollProgress.toFixed(3));
      
      // Apply blur effect if enabled
      if (this.enableBlur) {
        const maxBlur = 6; // Maximum blur in pixels
        const blurAmount = scrollProgress * maxBlur;
        this.style.setProperty('--background-scroll-blur', `${blurAmount}px`);
      }
      
      // Apply parallax effect if enabled
      if (this.enableParallax) {
        if (!this.blurElement) return;
        // Height-aware parallax to avoid "black bands" (image no longer covering its container).
        // We compute a desired translate based on element height and an intensity, then compute
        // the required scale to ensure coverage at max translate.
        const targetRect = this.blurElement.getBoundingClientRect();
        const height = Math.max(1, targetRect.height || this.blurElement.offsetHeight || this.offsetHeight || 1);

        // Match previous feel: the image ends ~50px shifted at full progress on a ~600px hero.
        const intensity = 0.7; // 0..1
        const baseShift = height * 0.12 * intensity;
        const desiredMaxShift = Math.min(140, Math.max(0, baseShift));

        // Compute scale needed to cover at max shift, with a small safety pad.
        let scale = 1 + (2 * desiredMaxShift) / height + 0.02;
        scale = Math.min(scale, 2);

        // Clamp translate to what the chosen scale can safely cover.
        const safeShift = ((scale - 1) * height) / 2;
        const maxShift = Math.min(desiredMaxShift, safeShift);

        // Hero moves "up" as you scroll down (negative translate)
        const translateY = -scrollProgress * maxShift;

        this.style.setProperty('--parallax-translate-y', `${translateY.toFixed(2)}px`);
        this.style.setProperty('--parallax-scale', scale.toFixed(3));
      }
    }

    /**
     * Throttle function to limit execution rate
     * @param {() => void} func - Function to throttle
     * @param {number} limit - Minimum time between calls in ms
     * @returns {EventListener} Throttled function
     */
    throttle(func, limit) {
      /** @type {boolean} */
      let inThrottle = false;
      return function () {
        if (!inThrottle) {
          func();
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    }
  }

  customElements.define('hero-animations', HeroAnimations);
}

// Re-initialize on Shopify theme editor events
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;
    const heroElement = target.querySelector('hero-animations');
    if (heroElement) {
      heroElement.classList.add('is-visible');
    }
  });
}

